import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import SignedInBar from "@/components/SignedInBar";
import ThemeToggle from "@/components/ThemeToggle";
import { useSession } from "@/lib/session";

/**
 * The frame around every signed-out page: the landing page, sign-in, the
 * privacy policy and the terms. Deliberately plainer than the app shell —
 * no bottom nav, because there is no vessel to navigate — and the same
 * paper ground and instrument type, so signing in does not feel like
 * arriving at a different product.
 */
export default function PublicShell({
  children,
  contactEmail,
}: {
  children: ReactNode;
  contactEmail?: string;
}) {
  const { status } = useSession();

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Signed in, a public page is still inside the app: it carries the
          full bar, with the links pointing at the pool you last looked at. */}
      {status.authenticated ? (
        <SignedInBar />
      ) : (
        <header
          className="border-b border-(--color-border-subtle)"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 px-4 py-3">
            <Wordmark />
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Link to="/login" className="btn btn-primary px-4 py-2 text-sm">
                Sign in
              </Link>
            </div>
          </div>
        </header>
      )}

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8">{children}</main>

      <footer className="border-t border-(--color-border-subtle)">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-x-5 gap-y-2 px-4 py-5 text-sm text-(--color-ink-muted)">
          <Link to="/guide" className="hover:text-(--color-ink)">
            Guide
          </Link>
          <Link to="/privacy" className="hover:text-(--color-ink)">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-(--color-ink)">
            Terms
          </Link>
          {contactEmail && (
            <a href={`mailto:${contactEmail}`} className="hover:text-(--color-ink)">
              {contactEmail}
            </a>
          )}
          <span className="ml-auto unit">Pool Nerd</span>
        </div>
      </footer>
    </div>
  );
}

/** The name, with the three reagent colours as a quiet mark beside it.
 *  `compact` keeps only the mark on a phone, where the bar also has to hold
 *  the pool's name. */
export function Wordmark({ large = false, compact = false }: { large?: boolean; compact?: boolean }) {
  return (
    <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="Pool Nerd">
      <span aria-hidden className="flex items-end gap-0.5">
        <span
          className="block w-1.5 rounded-full bg-(--color-reagent-dpd)"
          style={{ height: large ? 22 : 14 }}
        />
        <span
          className="block w-1.5 rounded-full bg-(--color-reagent-phenol)"
          style={{ height: large ? 30 : 20 }}
        />
        <span
          className="block w-1.5 rounded-full bg-(--color-reagent-bromocresol)"
          style={{ height: large ? 16 : 10 }}
        />
      </span>
      <span
        className={`font-semibold tracking-tight ${large ? "text-2xl" : "text-lg"} ${compact ? "hidden sm:inline" : ""}`}
      >
        Pool Nerd
      </span>
    </Link>
  );
}
